import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { otpVerification, resendOtp } from "../services/forgotpasswordApi";
import { AuthLayout } from "../components/layout/AuthLayout";

function OTPVerification() {
  const [otp, setOtp] = useState(["", "", "", "", ""]);
  const [isSubmitEnabled, setSubmitEnabled] = useState(false);
  const [timer, setTimer] = useState(600);
  const [canResend, setCanResend] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email || "";
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => setTimer(timer - 1), 1000);
      return () => clearInterval(interval);
    } else {
      setCanResend(true);
    }
  }, [timer]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const newOtpValues = [...otp];
    newOtpValues[index] = e.target.value.slice(0, 1);
    setOtp(newOtpValues);

    const allFilled = newOtpValues.every((value) => value.trim() !== "");
    setSubmitEnabled(allFilled);

    if (e.target.value && index < 4) {
      const nextInput = document.getElementById(`otp-input-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-input-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage("");
    setError("");

    const formattedOtp = otp.join("");

    const payload = {
      emailAddress: email,
      otp: formattedOtp,
    };

    setLoading(true);
    try {
      const response = await otpVerification(payload);

      if (response.data === true) {
        // The API re-verifies the OTP on reset, so it travels with the email.
        navigate("/resetpassword", { state: { email, otp: formattedOtp } });
      } else {
        setError(response.message);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setMessage("");
    setError("");
    setCanResend(false);
    setTimer(600);

    try {
      const emailTemplate = "ForgotPassword";
      const response = await resendOtp({ emailAddress: email, emailTemplate });
      if (response.succeeded) {
        setMessage("OTP has been resent successfully.");
      } else {
        setError(response.message || "Failed to resend OTP. Please try again.");
      }
    } catch (err: any) {
      setError(err.message || "An error occurred. Please try again.");
    }
  };

  return (
    <AuthLayout
      title={
        <>
          Enter <span className="auth-title__accent">OTP</span>
        </>
      }
      subtitle={
        <>
          OTP sent to: <strong>{email}</strong>
        </>
      }
    >
      {error ? (
        <div className="auth-alert auth-alert--error" role="alert">
          {error}
        </div>
      ) : null}
      {message ? (
        <div className="auth-alert auth-alert--success" role="alert">
          {message}
        </div>
      ) : null}

      <form className="auth-form" onSubmit={handleSubmit}>
        <div className="auth-otp-row" role="group" aria-label="One-time password">
          {otp.map((value, index) => (
            <input
              key={index}
              id={`otp-input-${index}`}
              className="auth-field__input auth-otp-digit"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder=" "
              value={value}
              maxLength={1}
              onChange={(e) => handleInputChange(e, index)}
              onKeyDown={(e) => handleKeyPress(e, index)}
              aria-label={`Digit ${index + 1}`}
            />
          ))}
        </div>

        <div className="auth-otp-meta">
          {timer > 0 ? (
            <p style={{ margin: 0 }}>Resend OTP in: {formatTime(timer)}</p>
          ) : (
            <button
              type="button"
              className="auth-link"
              onClick={handleResendOtp}
              disabled={!canResend}
            >
              Resend OTP
            </button>
          )}
        </div>

        <button
          type="submit"
          className={`auth-submit${loading ? " auth-submit--loading" : ""}`}
          disabled={!isSubmitEnabled || loading}
        >
          {loading ? "Submitting…" : "Submit"}
        </button>
      </form>
    </AuthLayout>
  );
}

export default OTPVerification;
